import {defineStore} from 'pinia'
import {formattedPrice} from "../app";
import {useOrderStore} from "./order";
import {useLoadingStore} from "./loading";

// You can name the return value of `defineStore()` anything you want,
// but it's best to use the name of the store and surround it with `use`
// and `Store` (e.g. `useUserStore`, `useCartStore`, `useProductStore`)
// the first argument is a unique id of the store across your application
export const useShipmentStore = (shipmentUUID) => defineStore(`shipments/${shipmentUUID}`, {
    state: () => {
        return ({
            uuid: shipmentUUID,
            needs_insurance: false,
            signature_confirmation: false,
            ref_id: null,
            selected_rate_id: null,
            is_residential: false,
            meta: null,
            label_url: null,
            tracking_number: null,
            rates: [],
            rates_errors: null,
            estimated_days: null,
            parcels: [],
            should_validate_address: true,
            initially_expanded: true,
            broker: null,
            unused: true,
            address_to: {
                name: '',
                street1: '',
                street2: '',
                phone: '',
                city: '',
                state: '',
                zip: '',
                country: 'US',
            },
        });
    },

    getters: {
        needsBroker: function (state) {
            return state.totalValue > 800
        },
        totalValue: function (state) {
            return state.parcels.reduce((total, parcel) => {
                return total + parseFloat(parcel.value || 0);
            }, 0);
        },
        totalWeight: function (state) {
            return state.parcels.reduce((total, parcel) => {
                return total + parseFloat(parcel.weight || 0);
            }, 0);
        },
        totalWeightWithUnit: function (state) {
            if (!this.totalWeight) return null;

            return `${this.totalWeight} ${state.parcels[0].mass_unit}`;
        },
        name: function (state) {
            return state?.address_to?.name || null;
        },
        location: function(state){
            const location = [state?.address_to?.city, state?.address_to?.state].filter(n => n).join(', ');
            return [location, state.totalWeightWithUnit].filter(n => n).join(' - ') || null;
        },
        readyForPurchase: function (state) {
            return state.selected_rate_id !== null;
        },
        selectedRate: function (state) {
            return state?.rates?.find(rate => rate.id === state.selected_rate_id);
        },
        selectedRateName: function (state) {
            if (!this.selectedRate) return null;

            const rate = this.selectedRate;
            return `${rate?.name} - ${formattedPrice(rate.total, rate.currency)} ${rate.currency}`
        },
        price: function (state) {
            return this.selectedRate?.total ?? 0;
        }
    },

    actions: {
        removeParcel: function (index) {
            let parcels = [...this.parcels];
            parcels.splice(index, 1);
            this.parcels = parcels
        },

        addParcel: function (values = {}) {
            this.parcels.push({
                id: null,
                weight: null,
                length: null,
                width: null,
                height: null,
                value: null,
                distance_unit: 'in',
                mass_unit: 'lb',
                ...values,
            });
        },
        upsertShipment: async function (formData, orderUUID) {
            const useOrder = useOrderStore();

            const useLoading = useLoadingStore();
            useLoading.isLoading = true;
            // console.log(, this.$state);
            const response = await axios.post(`/orders/${orderUUID}/shipments/${this.uuid}`, {shipments: [this.$state], '_method': 'put'}, {headers: {"Accept": "application/json"}})
                .then(response => {
                    useOrder.updateOrderStore(response.data);
                }).catch((error) => {
                    let message = '';
                    for(const prop in error.response.data){
                        message += error.response.data[prop].join("\n") + "\n\n";
                    }

                    alert(message);
                }).finally(() => {
                    useLoading.isLoading = false;
                })

            return response;
        },
        patchShipment: function(data, orderUUID){
            const useOrder = useOrderStore();

            const useLoading = useLoadingStore();
            useLoading.isLoading = true;

            return axios.post(`/orders/${orderUUID}/shipments/${this.uuid}`, {...data, '_method': 'patch'}, {headers: {"Accept": "application/json"}})
                .then(response => {
                    useOrder.updateOrderStore(response.data);
                }).finally(() => {
                    useLoading.isLoading = false;
                })
        },
        deleteRates: function(orderUUID){
            const useOrder = useOrderStore();
            const useLoading = useLoadingStore();
            useLoading.isLoading = true;
            this.should_validate_address = true;

            return axios.post(`/orders/${orderUUID}/shipments/${this.uuid}/deleteRates`, {'_method': 'delete'}, {headers: {"Accept": "application/json"}})
                .then(response => {
                    useOrder.updateOrderStore(response.data);
                }).finally(() => {
                    useLoading.isLoading = false;
                });
        },
        useCorrectedAddress: function (address) {
            const formatted = {};
            for (const [key, value] of Object.entries(address)) {
                formatted[key.replace('address_to_', '')] = value;
            }

            this.address_to = {...this.address_to, ...formatted};
        }
    },
})()
